import dayjs from "dayjs";
import { Link } from "react-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import relativeTime from "dayjs/plugin/relativeTime";
import World from "@app/icons/World";
import ProfileButton from "./ProfileButton";
import ThumbUp from "@app/icons/Thumb";
import { formatWord } from "@app/utils/post";
import Comment from "./Comment";
import Star from "@app/icons/Star";
import PdfIcon from "./PdfIcon";
import PostCardUserPopover from "./PostCardUserPopover";
import useUser from "@app/zustand/user";
import { PopulatedPost } from "@api/src/types/post";
import { PostResponse } from "@api/src/types/responses";
import { UserSearched } from "@api/src/types/user";
import getRoomName from "@app/utils/get-room-name";
import { PostAction } from "@app/types/post";
import { Poll } from "@prisma/client";
import { Bars3CenterLeftIcon } from "@heroicons/react/24/outline";
import useMedspotStore from "@app/zustand/store";
import useMinimumWidth from "@app/utils/useMinimumWidth";
import UserSearch from "./UserSearch";
dayjs.extend(relativeTime);
// Start observing visbility of element. On change, the
//   the callback is called with Boolean visibility as
//   argument:

function respondToVisibility(element: HTMLElement | null, callback: (visible: boolean) => void) {
  if (!element) return;
  const options = {
    root: document.documentElement,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      callback(entry.intersectionRatio > 0);
    });
  }, options);

  observer.observe(element);
}

interface PostCardProps {
  post: PopulatedPost;
  showRoomName?: boolean;
}

// https://tailwindcomponents.com/component/linkedin-post
export default function PostCard({ post, showRoomName = false }: PostCardProps) {
  const me = useUser((state) => state.user!);
  const [postData, setPostData] = useState(post);
  const commentFormRef = useRef<HTMLFormElement | null>(null);
  const postRef = useRef<HTMLElement | null>(null);
  const isDrawerOpen = useMedspotStore((state) => state.drawerOpen);

  // Individual loading states
  const [isCommenting, setIsCommenting] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isUnliking, setIsUnliking] = useState(false);
  const [isFaving, setIsFaving] = useState(false);
  const [isUnfaving, setIsUnfaving] = useState(false);

  const [quotedUsers, setQuotedUsers] = useState<Array<UserSearched>>([]);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    id,
    author,
    roomId,
    content,
    createdAt,
    likes,
    isLiked,
    isFaved,
    isAuthor,
    comments,
    quotedUsersInPost,
    seen,
  } = postData;

  const showAsLiked = !isUnliking && (isLiked || isLiking);
  const showAsFav = !isUnfaving && (isFaved || isFaving);
  const showNumberOfLikes = likes + (isLiking ? 1 : 0) - (isUnliking ? 1 : 0);
  const showAsComments = comments?.length
    ? `${comments?.length} commentaire${comments?.length > 1 ? "s" : ""}`
    : "Commenter";

  const handlePostAction = async ({ action, content }: PostAction) => {
    // Set the appropriate loading state
    switch (action) {
      case "comment":
        setIsCommenting(true);
        break;
      case "like":
        setIsLiking(true);
        break;
      case "unlike":
        setIsUnliking(true);
        break;
      case "fav":
        setIsFaving(true);
        break;
      case "unfav":
        setIsUnfaving(true);
        break;
    }

    try {
      await fetch(`${import.meta.env.VITE_API_URL}/post/${post.id}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          postId: post.id,
          roomId: post.roomId,
          authorId: post.author.id,
          content,
          quotedUsers: quotedUsers
            .filter((u) => content?.includes(displayQuotedUser(u)))
            .map((u) => u.id)
            .join(","),
        }),
      })
        .then(async (response) => response.json() as Promise<PostResponse>)
        .then((response) => {
          if (!response.ok) {
            console.error("Failed to perform post action:", response, {
              action,
              postId: post.id,
              roomId: post.roomId,
              authorId: post.author.id,
              content,
            });
          } else {
            if (response?.data?.post?.id) {
              setPostData(response.data.post);
            }
          }
        });
    } catch (error) {
      console.error("Failed to perform post action:", error);
    } finally {
      // Reset all loading states
      setIsCommenting(false);
      setIsLiking(false);
      setIsUnliking(false);
      setIsFaving(false);
      setIsUnfaving(false);
    }
  };

  const outsideLink = useMemo(() => {
    if (!postData.linkMetadata?.url) return null;
    const link = new URL(postData.linkMetadata?.url);
    link.searchParams.set("utm_source", "medspot.fr");
    return link.toString();
  }, [postData.linkMetadata?.url]);

  const [showComments, setShowComments] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const hasBeenVisible = useRef(false);
  const isDesktop = useMinimumWidth("md");
  useEffect(() => {
    respondToVisibility(postRef.current, (visible) => {
      if (visible && !hasBeenVisible.current && (isDesktop || !isDrawerOpen)) {
        handlePostAction({ action: "seen" });
        hasBeenVisible.current = true;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isDesktop]);

  const roomRelation = me.rooms?.find((r) => r.roomId === post.roomId);

  function displayQuotedUser(user: UserSearched) {
    return `@${user.firstName} ${user.lastName} `;
  }

  const handleUserSelect = (user: UserSearched) => {
    if (!textareaRef.current) return;

    const content = textareaRef.current.value;
    const cursorPos = textareaRef.current.selectionStart;
    const newContent = content.slice(0, cursorPos - 1) + displayQuotedUser(user) + content.slice(cursorPos);

    textareaRef.current.value = newContent;
    setShowUserSearch(false);
    setQuotedUsers((prev) => [...prev, user]);
    textareaRef.current.focus();
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    //window.sessionStorage.setItem("newPostContent", e.currentTarget.value);

    const cursorPos = e.target.selectionStart;
    const content = e.target.value;

    // Check if @ was just typed
    if (content[cursorPos - 1] === "@") {
      setShowUserSearch(true);
    }
  };

  if (isDeleted) {
    return null;
  }

  return (
    <article
      className="mt-2 flex w-full flex-col justify-center rounded-lg border border-gray-100 bg-white py-3 shadow-sm"
      ref={postRef}
    >
      <div className="flex items-start px-4">
        {postData.anonymous ? (
          <>
            <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-sky-900 text-4xl text-sky-900 text-opacity-25">
              🫥
            </span>
            <div className="ml-2">
              <div className="text-sm ">
                <span className="font-semibold capitalize">Publication anonyme</span>
                {/* <span className="text-gray-500"> • 1st</span> */}
              </div>
              <div className="flex flex-row gap-2 text-xs text-gray-500">
                <span className="inline-block">{dayjs().to(dayjs(createdAt), true)}</span>{" "}
                <span aria-hidden="true">{" · "}</span>
                <World />
              </div>
            </div>
          </>
        ) : (
          <>
            <Link prefetch="intent" to={{ pathname: `/profile/${author.plausible_id}` }} className="mt-1">
              <ProfileButton user={author} />
            </Link>
            <div className="ml-2">
              <Link
                prefetch="intent"
                to={{ pathname: `/profile/${author.plausible_id}` }}
                className="text-sm "
              >
                <span className="font-semibold capitalize">
                  {author.firstName} {author.lastName}{" "}
                </span>
                {/* <span className="text-gray-500"> • 1st</span> */}
              </Link>
              {author.firstName !== "- Medspot" && (
                <div className="flex flex-col">
                  <p className="m-0 line-clamp-1 text-xs text-gray-500 sm:line-clamp-2">
                    {author.status_pro} - {author.specialite}
                    {/* {!!author.medPoints && (
                    <>
                      {" - "}
                      <span className="text-xs font-normal italic text-gray-500">{author.medPoints} MedPoints</span>
                    </>
                  )} */}
                  </p>

                  {/* <p className="m-0 line-clamp-1 text-xs text-gray-500 sm:line-clamp-2">{author.place}</p> */}
                </div>
              )}
              <div className="flex flex-row gap-2 text-xs text-gray-500">
                <span className="inline-block">{dayjs().to(dayjs(createdAt), true)}</span>{" "}
                <span aria-hidden="true">{" · "}</span>
                <World />
              </div>
            </div>
          </>
        )}
        <form
          method="post"
          className="ml-auto text-sky-900"
          onSubmit={(e) => {
            e.preventDefault();
            handlePostAction({ action: isFaved ? "unfav" : "fav" });
          }}
        >
          <button type="submit" disabled={isFaving || isUnfaving}>
            <Star filled={showAsFav} className="pointer-events-none size-5" />
          </button>
        </form>
        <div className="ml-2 flex flex-row items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              className="text-gray-500 hover:text-sky-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            {showDropdown && (
              <div className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                <form
                  method="post"
                  onSubmit={(e) => {
                    e.preventDefault();
                    fetch(`${import.meta.env.VITE_API_URL}/post/${id}`, {
                      method: "POST",
                      credentials: "include",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        action: "report",
                        postId: id,
                        authorId: author.id,
                        roomId: roomId,
                      }),
                    });
                  }}
                >
                  <button
                    type="submit"
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setShowDropdown(false);
                      alert("Merci pour votre signalement, nous l'examinerons dans les plus brefs délais.");
                    }}
                  >
                    Signaler
                  </button>
                </form>
                {!!isAuthor && (
                  <button
                    type="button"
                    form="delete-post-form"
                    onClick={() => {
                      setShowDropdown(false);
                      if (
                        !window.confirm(
                          "Voulez-vous vraiment supprimer ce post ? Cette opération est irréversible"
                        )
                      ) {
                        return;
                      }
                      fetch(`${import.meta.env.VITE_API_URL}/post/${id}`, {
                        method: "POST",
                        credentials: "include",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          action: "delete",
                          postId: id,
                          authorId: author.id,
                          roomId: roomId,
                        }),
                      });
                      setShowDropdown(false);
                      setIsDeleted(true);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showRoomName && roomRelation && (
        <div className="border-l-4 border-sky-900 px-4 mx-4 mt-2 text-sm opacity-50">
          <span>Publié dans&nbsp;: </span>
          <span className="inline-block text-sky-900">{getRoomName(roomRelation)}</span>
        </div>
      )}
      <p className="mt-2 overflow-hidden text-ellipsis px-4 text-sm leading-normal text-gray-800 md:leading-relaxed">
        {content.split("\n").map((sentence, i) => (
          <React.Fragment key={i}>
            {sentence.split(" ").map(formatWord)}
            <br />
          </React.Fragment>
        ))}
      </p>
      {!!quotedUsersInPost?.length && (
        <div className="flex items-center px-4 text-xs">
          <span>- avec{`\u00A0`}</span>
          {quotedUsersInPost.map(({ quotedUser }, i, arr) => (
            <React.Fragment key={quotedUser.id}>
              {i > 0 ? (i === arr.length - 1 ? ` et\u00A0` : ", ") : ""}
              <Link
                to={`/profile/${quotedUser.plausible_id}`}
                prefetch="intent"
                className="font-bold text-sky-900 opacity-85"
              >
                {quotedUser.firstName} {quotedUser.lastName}
                {`\u00A0`}
              </Link>
            </React.Fragment>
          ))}
        </div>
      )}
      {!!postData.poll && (
        <PollDisplay
          poll={postData.poll}
          key={postData.poll.updatedAt as unknown as string}
          userPoll={postData.userPoll}
          setPostData={setPostData}
        />
      )}
      {!!postData.linkMetadata?.image && outsideLink && (
        <button
          type="button"
          className="mt-2 flex w-full flex-col overflow-hidden"
          onClick={() => {
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  type: "open-external-link",
                  url: outsideLink,
                })
              );
            } else {
              window.open(outsideLink, "_blank");
            }
          }}
        >
          <img
            src={postData.linkMetadata.image}
            alt={
              postData.linkMetadata.imageAlt ??
              postData.linkMetadata.description ??
              postData.linkMetadata.image
            }
            loading="lazy"
            className="flex-0 aspect-link max-h-[50vh] w-full object-contain"
          />
          <div className="flex flex-col bg-gray-100 px-4 py-3">
            <span className="font-sansSerif text-xs font-normal uppercase opacity-50">
              {postData.linkMetadata.publisher}
            </span>
            <span className="font-sansSerif line-clamp-2 font-semibold">{postData.linkMetadata.title}</span>
          </div>
        </button>
      )}
      {!!postData.attachments?.length && (
        <div
          className={`mt-2 px-4 ${postData.attachments.length > 1 ? "grid grid-cols-3 gap-2" : "flex justify-center"}`}
        >
          {postData.attachments.map((attachment) => (
            <div
              key={attachment}
              className={[
                "flex flex-col",
                postData.attachments.length > 1 ? "aspect-[4/3]" : "w-full max-w-2xl max-h-[50vh]",
                postData.attachments.length > 1 ? "" : attachment.endsWith(".pdf") ? "" : "min-h-[200px]",
              ].join(" ")}
            >
              {attachment.endsWith(".pdf") ? (
                <div className="group relative w-full h-full rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex flex-row items-center justify-center">
                  <div className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-sky-900 hover:bg-sky-50">
                    <PdfIcon className={postData.attachments.length > 1 ? "size-10 shrink-0" : "size-4"} />
                    <span className={postData.attachments.length > 1 ? "hidden sm:block" : ""}>
                      Cliquez ici pour voir le PDF
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                          JSON.stringify({
                            type: "open-external-link",
                            url: attachment,
                          })
                        );
                      } else {
                        window.open(attachment, "_blank");
                      }
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                      />
                    </svg>
                  </button>
                </div>
              ) : ["mp4", "mov", "avi", "webm", "mkv", "m4v", "wmv", "flv", "3gp"].includes(
                  attachment.split(".").at(-1)!
                ) ? (
                <video
                  src={attachment}
                  controls
                  className={`w-full rounded-lg ${
                    postData.attachments.length > 1
                      ? "h-full object-cover"
                      : "min-h-[200px] max-h-[50vh] object-contain"
                  }`}
                />
              ) : (
                <div
                  className={`relative group flex items-center justify-center bg-gray-50 rounded-lg ${
                    postData.attachments.length > 1 ? "h-full" : "min-h-[200px] max-h-[50vh]"
                  }`}
                >
                  <img
                    src={attachment}
                    alt={attachment}
                    loading="lazy"
                    className="rounded-lg max-h-full max-w-full object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                          JSON.stringify({
                            type: "open-external-link",
                            url: attachment,
                          })
                        );
                      }
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault();
          handlePostAction({ action: isLiked ? "unlike" : "like" });
        }}
      >
        <div className="mt-3 flex items-center px-4 text-xs text-sky-900">
          <button type="submit" disabled={isLiking || isUnliking}>
            <ThumbUp fill={showAsLiked ? "currentColor" : "none"} />
          </button>
          {showNumberOfLikes > 0 ? (
            <PostCardUserPopover
              showNumberOfLikes={showNumberOfLikes}
              postId={id}
              usersThatLiked={postData.usersThatLiked}
            />
          ) : (
            <span className="px-2">{showNumberOfLikes}</span>
          )}
          <Bars3CenterLeftIcon className="size-4 -rotate-90 ml-2" />
          <span className="px-2">{seen}</span>
          <button type="button" className="ml-auto" onClick={() => setShowComments(!showComments)}>
            {!showComments ? showAsComments : "Masquer les commentaires"}
          </button>
        </div>
      </form>
      {!!showComments && (
        <div className="relative">
          <form
            method="post"
            ref={commentFormRef}
            onSubmit={async (e) => {
              e.preventDefault();
              const content = new FormData(e.currentTarget).get("content") as string;
              await handlePostAction({ action: "comment", content });
              commentFormRef.current?.reset();
            }}
          >
            <input type="hidden" name="postId" value={id} />
            <input type="hidden" name="authorId" value={author.id} />
            <input type="hidden" name="roomId" value={roomId} />
            <div className="mt-3 flex items-start border-t border-sky-900 border-opacity-10 px-4 pt-2 text-xs text-sky-900">
              <ProfileButton user={me} />
              <div className="ml-1 flex w-full flex-col items-start">
                <textarea
                  ref={textareaRef}
                  name="content"
                  key={comments?.length}
                  placeholder="Commenter..."
                  className="min-h-9 w-full flex-grow rounded-lg border border-sky-900 border-opacity-30 px-4 py-2 text-left text-sky-900"
                  onChange={handleTextareaChange}
                />
                <button
                  type="submit"
                  disabled={isCommenting}
                  className="ml-auto mt-1 inline-block rounded-full border-none bg-sky-800 px-2 py-1 text-white"
                >
                  {isCommenting ? "En cours..." : "Publier"}
                </button>
              </div>
            </div>
          </form>
          {showUserSearch && (
            <div className="absolute right-4 top-6 z-20 w-72 rounded-lg bg-black border border-sky-100 shadow-lg">
              <UserSearch
                onSave={handleUserSelect}
                onClose={() => {
                  setShowUserSearch(false);
                  textareaRef.current?.focus();
                }}
                quotedUsers={quotedUsers}
              />
            </div>
          )}
          {comments?.map((comment) => (
            <Comment key={comment.id} comment={comment} handlePostAction={handlePostAction} />
          ))}
        </div>
      )}
    </article>
  );
}

function PollDisplay({
  poll,
  userPoll,
  setPostData,
}: {
  poll: Poll;
  userPoll: number | undefined;
  setPostData: (post: PopulatedPost) => void;
}) {
  const [isVoting, setIsVoting] = useState(false);
  const totalVotes =
    (poll.votes1 || 0) + (poll.votes2 || 0) + (poll.votes3 || 0) + (poll.votes4 || 0) + (poll.votes5 || 0);

  const getPercentage = (votes: number | null) => {
    if (!votes || !totalVotes) return 0;
    return Math.round((votes / totalVotes) * 100);
  };

  const percentages = {
    1: getPercentage(poll.votes1),
    2: getPercentage(poll.votes2),
    3: getPercentage(poll.votes3),
    4: getPercentage(poll.votes4),
    5: getPercentage(poll.votes5),
  };

  const maxPercentage = Math.max(...Object.values(percentages));

  const handleVote = async (optionNumber: number) => {
    setIsVoting(true);
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/poll/${poll.id}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ optionNumber, action: "vote" }),
      })
        .then(async (response) => response.json() as Promise<PostResponse>)
        .then((response) => {
          if (!response.ok) {
            alert(response.error);
          } else {
            if (response?.data?.post?.id) {
              setPostData(response.data.post);
            }
          }
        });
    } catch (error) {
      console.error("Failed to vote:", error);
    } finally {
      setIsVoting(false);
    }
  };

  const handleRemoveVote = async () => {
    setIsVoting(true);
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/poll/${poll.id}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "remove-vote" }),
      })
        .then(async (response) => response.json() as Promise<PostResponse>)
        .then((response) => {
          if (!response.ok) {
            alert(response.error);
          } else {
            if (response?.data?.post?.id) {
              setPostData(response.data.post);
            }
          }
        });
    } catch (error) {
      console.error("Failed to remove vote:", error);
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <div className="mt-4 flex flex-col gap-2 px-4">
      {/* Poll options */}
      <div className="flex flex-col gap-1.5">
        {[1, 2, 3, 4, 5].map((optionNumber) => {
          const optionText = poll[`option${optionNumber}` as keyof Poll] as string | null;
          if (!optionText) return null;

          const isWinner = percentages[optionNumber as keyof typeof percentages] === maxPercentage;
          const isSelected = userPoll === optionNumber;

          return (
            <button
              key={optionNumber}
              onClick={() => !userPoll && handleVote(optionNumber)}
              disabled={isVoting || !!userPoll}
              className={[
                "relative flex w-full items-center rounded-md px-3 py-1 text-left text-sm disabled:opacity-100 opacity-100 border-gray-200 border",
                userPoll ? "bg-gray-100 text-gray-500" : "hover:bg-gray-100",
              ].join(" ")}
            >
              {/* Progress bar background */}
              {userPoll && (
                <div
                  className={`absolute left-0 top-0 h-full rounded-md 
                    ${isWinner ? "bg-sky-900/90" : "bg-gray-200"}`}
                  style={{ width: `${percentages[optionNumber as keyof typeof percentages]}%` }}
                />
              )}
              {/* Option text and percentage */}
              <span className="relative z-10 flex w-full justify-between">
                <span className={[userPoll && isWinner ? "text-gray-100" : ""].join(" ")}>{optionText}</span>
                {userPoll && (
                  <span className={isWinner ? "px-2 bg-gray-100 rounded" : ""}>
                    {percentages[optionNumber as keyof typeof percentages]}%
                  </span>
                )}
              </span>
              {isSelected && <span className="relative z-10 ml-2 text-sky-900">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Vote count and remove vote button */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>
          {totalVotes} vote{totalVotes !== 1 ? "s" : ""}
        </span>
        {userPoll && (
          <>
            <span>•</span>
            <button onClick={handleRemoveVote} disabled={isVoting} className="text-sky-900 hover:underline">
              Retirer mon vote
            </button>
          </>
        )}
      </div>
    </div>
  );
}
